import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Search,
  List,
  FileText,
  Brain,
  Wrench,
  Pencil,
  Folder,
  FolderOpen,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { LoadingDots } from './LoadingDots';

export interface ToolCallStep {
  id: string;
  type: 'search' | 'analyze' | 'process' | 'tool';
  toolName?: string;
  description: string;
  status: 'pending' | 'running' | 'completed';
  args?: Record<string, unknown>;
  result?: unknown;
}

// Get icon for tool type
const getToolIcon = (step: ToolCallStep): React.FC<{ className?: string }> => {
  if (step.type === 'tool' && step.toolName) {
    switch (step.toolName) {
      case 'search_content':
        return Search;
      case 'list_recent_files':
        return List;
      case 'get_file_summary':
        return FileText;
      case 'get_file_transcript':
        return FileText;
      case 'get_user_memory':
        return Brain;
      case 'rename_file':
        return Pencil;
      case 'list_folders':
        return Folder;
      case 'list_folder_contents':
        return FolderOpen;
      case 'get_folder_info':
        return Info;
      default:
        return Wrench;
    }
  }
  return Search;
};

export interface ToolCallDisplayProps {
  thinkingText?: string;
  steps: ToolCallStep[];
  isVisible: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

const getStepStatusClass = (status: ToolCallStep['status']): string => {
  if (status === 'completed') return 'bg-primary';
  if (status === 'running') return 'bg-primary animate-pulse';
  return 'bg-muted-foreground/30';
};

const getStepTextClass = (status: ToolCallStep['status']): string => {
  if (status === 'pending') return 'text-muted-foreground';
  return 'text-foreground';
};

// Animated dots component for the header
const AnimatedDots: React.FC = () => (
  <span className="inline-flex">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="animate-bounce inline-block"
        style={{
          animationDelay: `${i * 0.15}s`,
          animationDuration: '1s',
        }}
      >
        .
      </span>
    ))}
  </span>
);

export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({
  thinkingText,
  steps,
  isVisible,
  defaultExpanded = true,
  className,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!isVisible) return null;

  const totalSteps = steps.length;
  const hasActiveStep = steps.some((s) => s.status === 'running');

  // Render the thinking text with animated dots
  const renderThinkingText = () => {
    const text =
      thinkingText ||
      t('chat.toolCall.thinking', 'Thinking about your request...');
    // Strip trailing dots and add animated ones when active
    const textWithoutDots = text.replace(/\.{2,}$/, '');
    return hasActiveStep || totalSteps === 0 ? (
      <>
        {textWithoutDots}
        <AnimatedDots />
      </>
    ) : (
      text
    );
  };

  // When there are no steps, show a simple non-collapsible header
  if (totalSteps === 0) {
    return (
      <div className={cn('w-full mb-3', className)}>
        <div className="flex items-center gap-2 w-full text-left px-2 py-1.5">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <LoadingDots dotSize={1} className="mr-1" />
            <span className="text-sm">{renderThinkingText()}</span>
          </div>
        </div>
      </div>
    );
  }

  // When there are steps, show the full collapsible UI
  return (
    <div className={cn('w-full mb-3', className)}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Thinking header - collapsible trigger */}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 w-full text-left group hover:bg-muted/50 rounded-md px-2 py-1.5 transition-colors"
          >
            <div className="flex items-center gap-1.5 text-muted-foreground">
              {hasActiveStep && <LoadingDots dotSize={1} className="mr-1" />}
              <span className="text-sm">{renderThinkingText()}</span>
            </div>
            <div className="ml-auto">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
          <div className="pl-2 mt-1">
            {/* Step count indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 pl-2">
              <span>
                {totalSteps === 1
                  ? t('chat.toolCall.stepCount.singular', '1 step')
                  : t('chat.toolCall.stepCount.plural', '{{count}} steps', {
                      count: totalSteps,
                    })}
              </span>
            </div>

            {/* Steps tree */}
            <div className="relative">
              {/* Vertical connector line */}
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

              {steps.map((step) => (
                <div
                  key={step.id}
                  className="relative flex items-start gap-2 py-1.5"
                >
                  {/* Bullet/connector */}
                  <div
                    className={cn(
                      'relative z-10 w-[7px] h-[7px] rounded-full mt-1.5 ml-[8px]',
                      getStepStatusClass(step.status),
                    )}
                  />

                  {/* Step content */}
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-sm', getStepTextClass(step.status))}>
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tool indicator */}
            {hasActiveStep && (
              <div className="flex items-center gap-2 mt-2 pl-2 py-1.5 text-muted-foreground">
                {(() => {
                  const activeStep = steps.find((s) => s.status === 'running');
                  const ToolIcon = activeStep
                    ? getToolIcon(activeStep)
                    : Search;
                  const toolLabel =
                    activeStep?.type === 'tool' && activeStep?.toolName
                      ? activeStep.toolName.replace(/_/g, ' ')
                      : t('chat.toolCall.assistantSearch', 'Assistant search');
                  return (
                    <div className="flex items-center gap-1.5 bg-muted/50 rounded-md px-2 py-1">
                      <ToolIcon className="h-3.5 w-3.5" />
                      <span className="text-xs capitalize">{toolLabel}</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default ToolCallDisplay;
